import { type AttributeCatalog, type Context, OPERATORS_BY_TYPE, findAttribute } from "./catalog";
import type {
  Audience,
  Condition,
  ConditionGroup,
  DevicePlacement,
  Placement,
  PlacementType,
  PublicMessage,
  Snapshot,
} from "./schema";

/**
 * In-app message engine — spec §Engine. Pure: the caller passes the snapshot, the context,
 * and the dismissed / seen maps it read from browser storage. Nothing here reads or writes.
 */

// ── Conditions ─────────────────────────────────────────────────────────────

/**
 * Glob with `*` = any run of characters, no other wildcard, anchored at both ends.
 * Two-pointer match, O(pattern × value) worst case: a regex built from `*` backtracks
 * exponentially on patterns like `*a*a*a*b`.
 */
export function globMatch(pattern: string, value: string): boolean {
  let p = 0;
  let v = 0;
  let star = -1;
  let starV = 0;
  while (v < value.length) {
    if (p < pattern.length && pattern[p] === "*") {
      star = p++;
      starV = v;
    } else if (p < pattern.length && pattern[p] === value[v]) {
      p++;
      v++;
    } else if (star >= 0) {
      p = star + 1;
      v = ++starV;
    } else return false;
  }
  while (p < pattern.length && pattern[p] === "*") p++;
  return p === pattern.length;
}

/**
 * Spec §Audience. False when the attribute is not in the catalog, when the operator does not
 * fit the attribute's type, when the context value has the wrong type, or when the condition
 * value has the wrong shape. An undefined (or null) context value fails every operator except
 * `notExists`.
 */
export function matchCondition(c: Condition, ctx: Context, catalog: AttributeCatalog): boolean {
  const def = findAttribute(catalog, c.attribute);
  if (!def || !OPERATORS_BY_TYPE[def.type].includes(c.op)) return false;

  const v = ctx[c.attribute];
  const missing = v === undefined || v === null;
  if (c.op === "notExists") return missing;
  if (missing) return false;
  if (c.op === "exists") return true;

  const want = c.value;
  switch (def.type) {
    case "string":
    case "enum": {
      if (typeof v !== "string") return false;
      if (c.op === "isOneOf") return Array.isArray(want) && (want as unknown[]).includes(v);
      if (typeof want !== "string") return false;
      if (c.op === "is") return v === want;
      if (c.op === "isNot") return v !== want;
      if (c.op === "contains") return v.includes(want);
      if (c.op === "matches") return globMatch(want, v);
      return false;
    }
    case "list": {
      if (!Array.isArray(v) || typeof want !== "string") return false;
      if (c.op === "contains") return v.includes(want);
      if (c.op === "notContains") return !v.includes(want);
      return false;
    }
    case "number": {
      if (typeof v !== "number" || !Number.isFinite(v)) return false;
      if (c.op === "between") {
        if (!Array.isArray(want) || want.length !== 2) return false;
        const [lo, hi] = want as unknown[];
        return typeof lo === "number" && typeof hi === "number" && v >= lo && v <= hi;
      }
      if (typeof want !== "number") return false;
      if (c.op === "eq") return v === want;
      if (c.op === "gt") return v > want;
      if (c.op === "lt") return v < want;
      return false;
    }
  }
}

/** `all` = every condition matches; `any` = at least one. An empty group never matches. */
export function matchGroup(g: ConditionGroup, ctx: Context, catalog: AttributeCatalog): boolean {
  if (g.conditions.length === 0) return false;
  return g.match === "all"
    ? g.conditions.every((c) => matchCondition(c, ctx, catalog))
    : g.conditions.some((c) => matchCondition(c, ctx, catalog));
}

/** `everyone`, or OR between groups (R12). */
export function matchAudience(a: Audience, ctx: Context, catalog: AttributeCatalog): boolean {
  if (a.mode === "everyone") return true;
  return a.groups.some((g) => matchGroup(g, ctx, catalog));
}

// ── Placement per device (R19) ─────────────────────────────────────────────

export type Device = "desktop" | "tablet" | "phone";

/** Fixed and shared by every app: phone < 600 px, tablet 600–1199 px, desktop ≥ 1200 px. */
export const BREAKPOINTS = { tablet: 600, desktop: 1200 } as const;

export function breakpointOf(width: number): Device {
  if (width >= BREAKPOINTS.desktop) return "desktop";
  if (width >= BREAKPOINTS.tablet) return "tablet";
  return "phone";
}

/** Follows `same` upward: phone → tablet → desktop. */
export function resolvePlacement(p: DevicePlacement, d: Device): Placement | "hidden" {
  if (d === "desktop") return p.desktop;
  if (d === "tablet") return p.tablet === "same" ? p.desktop : p.tablet;
  return p.phone === "same" ? resolvePlacement(p, "tablet") : p.phone;
}

// ── Selection ──────────────────────────────────────────────────────────────

export type VisibleMessage = { message: PublicMessage; placement: Placement };

export type VisibleBySlot = Record<Exclude<PlacementType, "feed">, VisibleMessage[]> & {
  /** Every kept `announcement`, `alsoInFeed` message, and message whose resolved placement is `feed`; newest first. */
  feed: PublicMessage[];
};

/** Spec §Engine, "How many render". `inline` is capped per anchor instead. */
export const SLOT_CAPS = {
  top: 1,
  "below-header": 1,
  bottom: 1,
  toast: 2,
  modal: 1,
  panel: 1,
} as const;

const SEVERITY_RANK: Record<string, number> = { outage: 3, warning: 2, info: 1 };

const newestFirst = (a: PublicMessage, b: PublicMessage) => (b.publishedAt ?? "").localeCompare(a.publishedAt ?? "");

/** Pinned first, then severity outage › warning › info › (announcement), then newest first. */
export function compareMessages(a: PublicMessage, b: PublicMessage): number {
  if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
  const bySeverity = (SEVERITY_RANK[b.severity ?? ""] ?? 0) - (SEVERITY_RANK[a.severity ?? ""] ?? 0);
  return bySeverity !== 0 ? bySeverity : newestFirst(a, b);
}

export type SelectVisibleInput = {
  snapshot: Snapshot;
  context: Context;
  catalog: AttributeCatalog;
  /** `{ [messageId]: dismissedAt }`, merged from both storages by the caller. */
  dismissed: Record<string, string>;
  /** `{ [messageId]: firstSeenAt }`. */
  seen: Record<string, string>;
  viewportWidth: number;
};

/** Context key holding the `data-ui` ids present in the DOM; an inline anchor needs its id here. */
export const UI_SURFACES_KEY = "page.uiSurfaces";

/**
 * Spec §Engine. A message reaches a slot when its audience matches, it is not dismissed, it is
 * not an already-seen `oncePerBrowser` message, and its placement for the current device is not
 * `hidden`. The slot is the resolved placement. The feed applies the same filter except the seen
 * one — a seen `oncePerBrowser` message stays listed, as read — and lists every kept
 * `announcement`, every kept `alsoInFeed` message, and every kept message whose resolved
 * placement is `feed`; newest first.
 */
export function selectVisible(input: SelectVisibleInput): VisibleBySlot {
  const { snapshot, context, catalog, dismissed, seen, viewportWidth } = input;
  const device = breakpointOf(viewportWidth);
  const out: VisibleBySlot = {
    top: [],
    "below-header": [],
    bottom: [],
    toast: [],
    modal: [],
    panel: [],
    inline: [],
    feed: [],
  };

  const surfacesValue = context[UI_SURFACES_KEY];
  const surfaces = new Set(Array.isArray(surfacesValue) ? surfacesValue : []);
  const anchorsTaken = new Set<string>();

  const kept = snapshot.messages.filter((m) => matchAudience(m.audience, context, catalog));

  for (const message of kept.sort(compareMessages)) {
    if (dismissed[message.id]) continue;
    const placement = resolvePlacement(message.placement, device);
    if (placement === "hidden") continue;
    if (message.kind === "announcement" || message.alsoInFeed || placement.type === "feed") out.feed.push(message);
    if (placement.type === "feed") continue;
    // Seen-once applies to the rendering slots only: the feed lists read items too (LOCKER-488).
    if (message.frequency === "oncePerBrowser" && seen[message.id]) continue;

    if (placement.type === "inline") {
      const { uiSurface, position } = placement.anchor;
      const anchorKey = `${uiSurface}|${position}`;
      if (!surfaces.has(uiSurface) || anchorsTaken.has(anchorKey)) continue;
      anchorsTaken.add(anchorKey);
      out.inline.push({ message, placement });
      continue;
    }
    const slot = out[placement.type];
    if (slot.length < SLOT_CAPS[placement.type]) slot.push({ message, placement });
  }

  out.feed.sort(newestFirst);
  return out;
}
